import * as RdxTabs from '@radix-ui/react-tabs';
import { ClassValue } from 'clsx';
import cn from '../../app/utils/cn';
import Select from './Select';

interface ITabsRootProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: ClassValue;
}

function TabsRoot({
  value,
  onValueChange,
  children,
  className,
}: ITabsRootProps) {
  return (
    <RdxTabs.Root
      value={value}
      onValueChange={onValueChange}
      className={cn(className)}
    >
      {children}
    </RdxTabs.Root>
  );
}

interface ITabsListProps {
  children: React.ReactNode;
  className?: ClassValue;
  'aria-label'?: string;
}

function TabsList({
  children,
  className,
  'aria-label': ariaLabel,
}: ITabsListProps) {
  return (
    <RdxTabs.List
      aria-label={ariaLabel}
      className={cn('border-border-input flex gap-2 border-b pb-2', className)}
    >
      {children}
    </RdxTabs.List>
  );
}

interface ITabsTriggerProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: ClassValue;
}

function TabsTrigger({
  value,
  children,
  disabled,
  className,
}: ITabsTriggerProps) {
  return (
    <RdxTabs.Trigger
      value={value}
      disabled={disabled}
      className={cn(
        'text-text-secondary hover:bg-hover-subtle inline-flex min-h-10 items-center justify-center rounded-md px-3 py-1 text-sm transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-green-500/50',
        'data-[state=active]:text-white-0 data-[state=active]:bg-green-600',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      {children}
    </RdxTabs.Trigger>
  );
}

interface ITabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: ClassValue;
}

function TabsContent({ value, children, className }: ITabsContentProps) {
  return (
    <RdxTabs.Content value={value} className={cn(className)}>
      {children}
    </RdxTabs.Content>
  );
}

export interface ITabItem {
  value: string;
  label: string;
}

interface ITabsNavProps {
  items: ITabItem[];
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  className?: ClassValue;
}

function TabsNav({
  items,
  value,
  onValueChange,
  label,
  className,
}: ITabsNavProps) {
  return (
    <>
      <div className="md:hidden">
        <Select
          placeholder={label}
          value={value}
          onChange={onValueChange}
          options={items.map((item) => ({
            value: item.value,
            text: item.label,
          }))}
        />
      </div>

      <TabsList className={cn('hidden md:flex', className)} aria-label={label}>
        {items.map((item) => (
          <TabsTrigger key={item.value} value={item.value}>
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </>
  );
}

export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
  Nav: TabsNav,
};
